import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import { tenantService } from '@/services/TenantService'
import { organizationKeys, tenantKeys } from '@/hooks/queryKeys'
import type { TenantDto, TenantFeatureChangeListDto, TenantListDto } from '@lib/dto/tenant.dto'
import type { TenantFeature } from '@lib/enums/tenant-feature.enum'
import type { UserDto, UserListDto } from '@lib/dto/user.dto'
import type { CreateTenantInput, UpdateTenantInput } from '@lib/input/tenant.input'
import type { InviteByEmailInput } from '@lib/input/user.input'

/** All tenants on the platform (super admin only). */
export function useTenants(page?: number): UseQueryResult<TenantListDto> {
  return useQuery({
    queryKey: tenantKeys.list(page),
    queryFn: () => tenantService.list(page),
    staleTime: 60_000,
    // The previous page's rows stay on screen while the next one loads — see
    // the note on `useVenues`.
    placeholderData: (previous) => previous,
  })
}

/** One tenant by id (super admin only). Disabled when `tenantId` is empty. */
export function useTenant(tenantId: string): UseQueryResult<TenantDto> {
  return useQuery({
    queryKey: tenantKeys.detail(tenantId),
    queryFn: () => tenantService.get(tenantId),
    staleTime: 60_000,
    enabled: tenantId.length > 0,
  })
}

/** Create a tenant, refreshing the list on success. */
export function useCreateTenant(): UseMutationResult<TenantDto, Error, CreateTenantInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateTenantInput) => tenantService.create(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tenantKeys.lists() })
    },
  })
}

/** Rename a tenant, refreshing list + detail on success. */
export function useUpdateTenant(): UseMutationResult<TenantDto, Error, { id: string; input: UpdateTenantInput }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateTenantInput }) => tenantService.update(id, input),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: tenantKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: tenantKeys.detail(vars.id) })
    },
  })
}

/** Every time a feature was switched for a tenant, newest first (super admin only). */
export function useTenantFeatureHistory(tenantId: string): UseQueryResult<TenantFeatureChangeListDto> {
  return useQuery({
    queryKey: tenantKeys.featureChanges(tenantId),
    queryFn: () => tenantService.featureHistory(tenantId),
    staleTime: 30_000,
    enabled: tenantId.length > 0,
  })
}

/**
 * Switch a feature for a tenant. Refreshes the tenant, its history, and the
 * organization view — the super admin may be in support mode inside this very
 * tenant, looking at the tab they just switched off.
 */
export function useSetTenantFeature(
  tenantId: string,
): UseMutationResult<TenantDto, Error, { feature: TenantFeature; enabled: boolean }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ feature, enabled }: { feature: TenantFeature; enabled: boolean }) =>
      tenantService.setFeature(tenantId, feature, enabled),
    onSuccess: (tenant) => {
      queryClient.setQueryData(tenantKeys.detail(tenantId), tenant)
      void queryClient.invalidateQueries({ queryKey: tenantKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: tenantKeys.featureChanges(tenantId) })
      void queryClient.invalidateQueries({ queryKey: organizationKeys.detail(tenantId) })
    },
  })
}

/** Choose or move a tenant's billing owner (super admin only). */
export function useSetTenantBillingOwner(tenantId: string): UseMutationResult<TenantDto, Error, { userId: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ userId }: { userId: string }) => tenantService.setBillingOwner(tenantId, userId),
    onSuccess: (tenant) => {
      queryClient.setQueryData(tenantKeys.detail(tenantId), tenant)
      void queryClient.invalidateQueries({ queryKey: tenantKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: organizationKeys.detail(tenantId) })
    },
  })
}

/** Store a tenant the API just returned, and refresh everything that shows it. */
function useTenantRefresh(tenantId: string): (tenant: TenantDto) => void {
  const queryClient = useQueryClient()
  return (tenant) => {
    queryClient.setQueryData(tenantKeys.detail(tenantId), tenant)
    void queryClient.invalidateQueries({ queryKey: tenantKeys.lists() })
    void queryClient.invalidateQueries({ queryKey: organizationKeys.detail(tenantId) })
  }
}

/** Comp a tenant or end its comp (super admin only). */
export function useSetTenantComp(tenantId: string): UseMutationResult<TenantDto, Error, { comped: boolean }> {
  const refresh = useTenantRefresh(tenantId)
  return useMutation({
    mutationFn: ({ comped }: { comped: boolean }) => tenantService.setComp(tenantId, comped),
    onSuccess: refresh,
  })
}

/** Add days to a tenant's trial or grace period (super admin only). */
export function useExtendTenantBilling(
  tenantId: string,
): UseMutationResult<TenantDto, Error, { what: 'trial' | 'grace'; days: number }> {
  const refresh = useTenantRefresh(tenantId)
  return useMutation({
    mutationFn: ({ what, days }: { what: 'trial' | 'grace'; days: number }) => tenantService.extend(tenantId, what, days),
    onSuccess: refresh,
  })
}

/** Users belonging to a tenant (super admin tenant detail). */
export function useTenantUsers(tenantId: string): UseQueryResult<UserListDto> {
  return useQuery({
    queryKey: tenantKeys.users(tenantId),
    queryFn: () => tenantService.listUsers(tenantId),
    staleTime: 30_000,
  })
}

function useTenantUsersInvalidation(tenantId: string): () => void {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: tenantKeys.users(tenantId) })
    void queryClient.invalidateQueries({ queryKey: tenantKeys.lists() })
    void queryClient.invalidateQueries({ queryKey: tenantKeys.detail(tenantId) })
  }
}

/** Invite an organization admin into a tenant. */
export function useInviteTenantAdmin(tenantId: string): UseMutationResult<UserDto, Error, InviteByEmailInput> {
  const invalidate = useTenantUsersInvalidation(tenantId)
  return useMutation({
    mutationFn: (input: InviteByEmailInput) => tenantService.inviteUser(tenantId, input),
    onSuccess: invalidate,
  })
}

/**
 * Withdraw an invitation nobody accepted, removing the user. Offered only for a
 * PENDING invite: deactivating one would leave a row for someone who never
 * arrived and keep hold of their email address, so a mistyped invite could never
 * be re-sent to the corrected one.
 */
export function useDeleteInvitedTenantUser(tenantId: string): UseMutationResult<void, Error, { userId: string }> {
  const invalidate = useTenantUsersInvalidation(tenantId)
  return useMutation({
    mutationFn: (vars: { userId: string }) => tenantService.deletePendingUser(tenantId, vars.userId),
    onSuccess: invalidate,
  })
}

export function useDeactivateTenantUser(tenantId: string): UseMutationResult<UserDto, Error, { userId: string }> {
  const invalidate = useTenantUsersInvalidation(tenantId)
  return useMutation({
    mutationFn: (vars: { userId: string }) => tenantService.deactivateUser(tenantId, vars.userId),
    onSuccess: invalidate,
  })
}

export function useReactivateTenantUser(tenantId: string): UseMutationResult<UserDto, Error, { userId: string }> {
  const invalidate = useTenantUsersInvalidation(tenantId)
  return useMutation({
    mutationFn: (vars: { userId: string }) => tenantService.reactivateUser(tenantId, vars.userId),
    onSuccess: invalidate,
  })
}

export function useResendTenantInvite(tenantId: string): UseMutationResult<void, Error, { userId: string }> {
  return useMutation({
    mutationFn: (vars: { userId: string }) => tenantService.resendInvite(tenantId, vars.userId),
  })
}
